import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Contato — Dedetizadora em Chapecó/SC");
const DESC = seoDescription(
  "Fale com a Dedetizadora em Chapecó: orçamento pelo WhatsApp, agendamento de vistoria técnica gratuita e atendimento de emergência para pragas urbanas.",
);

const CONTACT_FAQ = [
  { q: "Como pedir orçamento?", a: "O jeito mais rápido é chamar no WhatsApp descrevendo a praga (baratas, ratos, cupim etc.), o tamanho do imóvel e a região de Chapecó. Também dá para pedir vistoria técnica gratuita pelo formulário." },
  { q: "Fazem atendimento fora de Chapecó?", a: "Sim. Atendemos toda a região oeste catarinense: Chapecó, Xaxim, Xanxerê, Coronel Freitas, Cordilheira Alta, Guatambu, Nova Erechim e cidades próximas." },
  { q: "Vocês têm plantão de emergência?", a: "Sim, para casos críticos (escorpião em casa com criança, infestação grave em restaurante) atendemos no mesmo dia. Chame no WhatsApp." },
  { q: "Preciso desocupar o imóvel durante a aplicação?", a: "Depende do produto usado. Na maioria dos serviços residenciais é preciso ausentar-se de 2 a 4 horas. Explicamos tudo antes do serviço e no certificado entregue." },
];

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/contato" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/contato` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: `Contato — ${SITE.name}`,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            availableLanguage: "Portuguese",
            email: SITE.email,
            telephone: SITE.phone,
            areaServed: "Chapecó/SC",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CONTACT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Contato,
});

function Contato() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", assunto: "Orçamento — Dedetização residencial", mensagem: "" });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const subject = encodeURIComponent(`[Contato site] ${form.assunto} — ${form.nome}`);
    const body = encodeURIComponent(`${form.mensagem}\n\n— ${form.nome} (${form.email})`);
    window.open(`mailto:${SITE.email}?subject=${subject}&body=${body}`, "_blank", "noopener");
    setSubmitted(true);
  }

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Contato" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Fale com a gente</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">Orçamento e agendamento</span> em Chapecó
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Descreva a praga e o tamanho do imóvel — enviamos orçamento pelo WhatsApp ou agendamos vistoria técnica gratuita no seu endereço.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <form onSubmit={onSubmit} className="card-glass space-y-4">
              <div>
                <label htmlFor="nome" className="text-sm font-semibold">Nome</label>
                <input id="nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label htmlFor="email" className="text-sm font-semibold">E-mail ou telefone</label>
                <input id="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label htmlFor="assunto" className="text-sm font-semibold">Serviço</label>
                <select id="assunto" value={form.assunto} onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none">
                  <option>Orçamento — Dedetização residencial</option>
                  <option>Orçamento — Dedetização comercial</option>
                  <option>Desratização</option>
                  <option>Descupinização</option>
                  <option>Controle para condomínio</option>
                  <option>MIP para indústria / restaurante</option>
                  <option>Emergência (escorpião, infestação grave)</option>
                </select>
              </div>
              <div>
                <label htmlFor="mensagem" className="text-sm font-semibold">Detalhes (praga, endereço em Chapecó, tamanho do imóvel)</label>
                <textarea id="mensagem" rows={5} required value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                  className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
              </div>
              <button type="submit" className="btn-primary w-full">Enviar solicitação</button>
              {submitted && <p className="text-sm text-muted-foreground">Estamos abrindo seu e-mail. Se preferir, chame direto no WhatsApp {SITE.phone}.</p>}
            </form>
          </Reveal>

          <div className="space-y-4">
            <Reveal delay={1}>
              <div className="card-glass border-l-4 border-l-accent">
                <h2 className="text-lg font-semibold">WhatsApp — resposta imediata</h2>
                <p className="mt-2 text-sm text-muted-foreground">Forma mais rápida de receber orçamento:</p>
                <p className="mt-3 text-base font-semibold">{SITE.phone}</p>
                <div className="mt-4"><a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent w-full">Chamar no WhatsApp</a></div>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="card-glass border-l-4 border-l-accent">
                <h2 className="text-lg font-semibold">E-mail</h2>
                <p className="mt-3 text-base">
                  <a href={`mailto:${SITE.email}`} className="text-primary hover:underline font-semibold">{SITE.email}</a>
                </p>
                <p className="mt-3 text-xs text-muted-foreground">Resposta em até 24 horas úteis.</p>
              </div>
            </Reveal>
            <Reveal delay={3}>
              <div className="card-glass border-l-4 border-l-accent">
                <h2 className="text-lg font-semibold">Endereço e horário</h2>
                <p className="mt-2 text-sm text-muted-foreground">{SITE.address}</p>
                <p className="mt-2 text-sm text-muted-foreground">{SITE.hours}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section>
        <FAQ items={CONTACT_FAQ} />
      </Section>
    </>
  );
}
